/* eslint-disable sonarjs/no-duplicate-string */

import { Component, OnInit, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbilityService } from '@casl/angular';
import {
  AppAbilityService,
  AppSubjects,
  type AppAbility,
  type WithAuthorizationOptionsExcludingAction,
} from '@pflegix/authorization';
import { I18nService } from '@pflegix/i18n';
import { MenuItem } from 'primeng/api';
import { AppMenuitemComponent } from './app.menuitem.component';
import { appMenuTranslationsDe } from './i18n/de/app-menu.translations.de';
import { appMenuTranslationsEn } from './i18n/en/app-menu.translations.en';

const MENU_ITEMS: Array<WithAuthorizationOptionsExcludingAction<MenuItem>> = [
  {
    label: 'tickets',
    icon: 'fal tickets',
    routerLink: ['/tickets'],
    authorization: {
      subject: AppSubjects.ticketing,
    },
  },
  {
    label: 'leads',
    icon: 'fal address-book',
    routerLink: ['/leads'],
    authorization: {
      subject: AppSubjects.leadManagement,
    },
  },
  {
    label: 'customers',
    icon: 'fal users-medical',
    routerLink: ['/customers'],
    authorization: {
      subject: AppSubjects.customerManagement,
    },
  },
  {
    label: 'employees',
    icon: 'fal users-between-lines',
    routerLink: ['/employees'],
    authorization: {
      subject: AppSubjects.employeeManagement,
    },
  },
  {
    label: 'organizations',
    icon: 'fal sitemap',
    routerLink: ['/organizations'],
    authorization: {
      subject: AppSubjects.organizationManagement,
    },
  },
  /* {
    label: 'users',
    icon: 'fal user',
    routerLink: ['/users'],
    authorization: {
      subject: AppSubjects.userManagement,
    },
  }, */
  {
    label: 'dispatching',
    icon: 'fal calendar-lines-pen',
    routerLink: ['/dispatching'],
    authorization: {
      subject: AppSubjects.dispatching,
    },
  },
  {
    label: 'documentations',
    icon: 'fal signature',
    routerLink: ['/documentations'],
    authorization: {
      subject: AppSubjects.documentationManagement,
    },
  },
  {
    label: 'appointments',
    icon: 'fal calendar-check',
    routerLink: ['/appointments'],
    authorization: {
      subject: AppSubjects.appointmentManagement,
    },
  },
  {
    label: 'ratings',
    icon: 'fal star',
    routerLink: ['/ratings'],
    authorization: {
      subject: AppSubjects.ratingManagement,
    },
  },
  /* {
    label: 'accounting',
    icon: 'fal file-invoice-dollar',
    url: 'https://admin.testing4.pflegix.de/apps/finance/invoices/invoice-list',
    target: '_blank',
    authorization: {
      subject: AppSubjects.accounting,
    },
  }, */
  {
    label: 'analysis',
    icon: 'fal chart-line-up',
    url: 'https://metabase.pflegix.de',
    target: '_blank',
    authorization: {
      subject: AppSubjects.accounting,
    },
  },
  {
    label: 'settings',
    icon: 'fal gears',
    routerLink: ['/settings'],
    authorization: {
      subject: AppSubjects.settings,
    },
  },

  // ! TODO: REMOVE THIS MAPPING WHEN USING THE REAL BACKEND FOR AUTHORIZATION/PERMISSIONS
].map<MenuItem>((item) => ({ ...item, queryParamsHandling: 'merge' }));

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
  imports: [AppMenuitemComponent],
})
export class AppMenuComponent implements OnInit {
  protected menuItems = signal<Array<WithAuthorizationOptionsExcludingAction<MenuItem>>>([]);

  private readonly i18nService = inject(I18nService);

  private readonly abilityService = inject<AbilityService<AppAbility>>(AbilityService);

  private readonly appAbilityService = inject(AppAbilityService);

  private readonly ability = toSignal<AppAbility>(this.abilityService.ability$);

  constructor() {
    this.updateMenuItems();
    this.setupOnAbilityUpdated();
  }

  public ngOnInit(): void {
    this.i18nService.addTranslations({
      namespace: 'navigation',
      de: appMenuTranslationsDe,
      en: appMenuTranslationsEn,
    });
  }

  private updateMenuItems(): void {
    this.menuItems.set(
      this.appAbilityService.filterReadable(MENU_ITEMS, {
        keepItemsWithoutAuthorizationOptions: true,
      })
    );
  }

  private setupOnAbilityUpdated(): void {
    effect((onCleanup) => {
      const unsubscribeAbilityUpdated = this.ability()?.on('updated', () => {
        this.updateMenuItems();
      });

      onCleanup(() => {
        if (unsubscribeAbilityUpdated) {
          unsubscribeAbilityUpdated();
        }
      });
    });
  }
}
